import { Suspense } from 'react'
import { useCurrentRefinements } from 'react-instantsearch'
import { InView } from 'react-intersection-observer'
import { isEmpty } from 'remeda'
import { useAlgoliaFilters } from '@components/AlgoliaSearch/AlgoliaSearchProvider'
import { SearchResults } from '@components/AlgoliaSearch/SearchResults'
import { lazyImport } from '@helpers'
import type { CollectionMessageData } from '@velasca/types'
import { CollectionTextSection } from '@velasca/ui/collection-text-section'
import ApparelCollection from '../ApparelCollection/ApparelCollection'
import withLazyLoad from '../LazyLoad/LazyLoad'
import { CollectionContainer } from './styles'

const { RecentProductsSlider } = lazyImport(
  () => import('@components/RecentProductsSlider/RecentProductsSlider'),
  'RecentProductsSlider',
)

const lazyConfig = {
  rootMargin: `800px 0px 800px 0px`,
  triggerOnce: true,
}

const RecentProductsSliderLazyLoad = withLazyLoad<
  React.ComponentProps<typeof RecentProductsSlider>
>((props) => {
  return (
    <Suspense fallback={null}>
      <RecentProductsSlider {...props} />
    </Suspense>
  )
}, lazyConfig)

interface ApparelMacroCategoryProps {
  categories: { handle: string }[]
  productsCount: number[]
  recentProductsSettings: {
    enabled: boolean
  }
  collectionAltStringTemplate: string
  collectionTopMessageData?: CollectionMessageData
  showCollectionBannerOnMobile?: boolean
}

const ApparelMacroCategory = ({
  categories,
  productsCount,
  recentProductsSettings,
  collectionAltStringTemplate,
  collectionTopMessageData,
  showCollectionBannerOnMobile,
}: ApparelMacroCategoryProps) => {
  const { isSearchActive, searchOnPageLoad } = useAlgoliaFilters()

  const { items } = useCurrentRefinements()

  return (
    <>
      {collectionTopMessageData && !isEmpty(collectionTopMessageData) && (
        <CollectionTextSection
          title={collectionTopMessageData.title}
          upperTitle={collectionTopMessageData.upperTitle}
          paragraph={collectionTopMessageData.paragraph}
        />
      )}
      {/* Nel caso in cui si refreshi la pagina, c'è una casistica dove gli items sono vuoti per un istante. Flashiano quindi i risultati non filtrati restituiti da Shopify.
      Per evitare questa cosa, è stato introdotto il parametro searchOnPageLoad: il parametro dice che sicuramente vanno fatti vedere i risultati di ricerca, indipendentemente da cosa dicono gli items.
      Quando si disattivano tutti i filtri, devono tornare in vista i prodotti restituiti da Shopify. Per questo il parametro searchOnPageLoad va tenuto in considerazione solo finché
      la ricerca non viene toccata e "attivata" dall'utente. Da quel momento, se gli items sono vuoti, vanno fatti vedere i risultati di Shopify. */}

      {!isEmpty(items) || (!isSearchActive && searchOnPageLoad) ? (
        <SearchResults showProductType showReviewBadge />
      ) : (
        <div className="apparel-macro-collection">
          {categories.map(({ handle }, i) => (
            <CollectionContainer key={handle}>
              <InView {...lazyConfig}>
                {({ inView, ref }) => (
                  <ApparelCollection
                    showBanner
                    showSingleVariant
                    showProductType
                    showReviewBadge
                    groupProducts
                    shouldLoadAllProducts
                    ref={ref}
                    categoryHandle={handle}
                    productsCount={productsCount[i]}
                    skip={!inView}
                    collectionAltStringTemplate={collectionAltStringTemplate}
                    showCollectionBannerOnMobile={showCollectionBannerOnMobile}
                    skeletonLength={
                      productsCount?.[i] > 4 ? 4 : productsCount?.[i]
                    }
                  />
                )}
              </InView>
            </CollectionContainer>
          ))}
        </div>
      )}
      {recentProductsSettings.enabled && <RecentProductsSliderLazyLoad />}
    </>
  )
}

export default ApparelMacroCategory
